<template>
  <div class="container">
    <b-form class="m-4" @submit.stop.prevent="save">
      <b-row>
        <b-col xs="12" sm="12" md="6" lg="6" xl="6" cols="12">
          <b-form-group label-size="md">
            <template slot="label">
              <div>Select Vehicle</div>
            </template>
            <Multiselect
              v-model="selectedVehicle"
              :options="availableVehiclesList"
              :multiple="false"
              :close-on-select="true"
              :clear-on-select="true"
              :preserve-search="false"
              :internal-search="false"
              placeholder="Search by VIN or model"
              track-by="id"
              :preselect-first="false"
              :loading="isLoading"
              @search-change="searchVehicles"
              @input="addVehicleToSelected"
              selectLabel=""
            >
              <template #option="props">
                <div class="option__desc">
                  <div><strong>VIN:</strong> {{ props.option.vinNumber }}</div>
                  <div>{{ props.option.make }} {{ props.option.model }}</div>
                </div>
              </template>
              <template #singleLabel="props">
                <div class="option__desc">
                  <span
                    ><strong>VIN:</strong> {{ props.option.vinNumber }}</span
                  >
                  <span>
                    - {{ props.option.make }} {{ props.option.model }}</span
                  >
                </div>
              </template>
            </Multiselect>
          </b-form-group>
        </b-col>

        <b-col xs="12" sm="12" md="12" lg="12" xl="12" cols="12">
          <label>Description</label>
          <b-form-textarea
            id="textarea"
            v-model="form.description"
            placeholder=""
            rows="3"
            max-rows="6"
            class="mb-2 text-dark"
            autofocus
          ></b-form-textarea>
        </b-col>
      </b-row>

      <b-row class="mt-3" v-if="selectedVehicles.length > 0">
        <b-col cols="12">
          <h5>Selected Vehicles</h5>
          <b-table
            striped
            hover
            :items="selectedVehicles"
            :fields="vehicleFields"
            class="mt-2"
          >
            <template #cell(actions)="row">
              <b-button
                variant="danger"
                size="sm"
                @click="removeVehicle(row.index)"
                class="py-1"
              >
                <b-icon icon="trash" />
              </b-button>
            </template>
          </b-table>
        </b-col>
      </b-row>

      <b-row class="mt-3">
        <b-col sm="12" md="6" lg="6" xl="6" />
        <b-col sm="12" md="3" lg="3" xl="3" />
        <b-col sm="12" md="3" lg="3" xl="3">
          <b-btn
            v-if="userHasPermission('exportLoads', 'write')"
            block
            size="lg"
            type="submit"
            class="btn btn-success"
            :disabled="!isFormValid"
          >
            Submit
            <b-spinner v-if="isLoading" small variant="white" />
          </b-btn>
        </b-col>
      </b-row>
    </b-form>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { generalMixin } from "@/modules/mixin.js";
import Multiselect from "vue-multiselect";
import { superAdminMixin } from "../mixins";
import { GET_RE_IMPORT_VEHICLES, POST_REIMPORT_DATA } from "./actions";

export default {
  mixins: [generalMixin, superAdminMixin],
  props: ["reImportItem", "refreshData", "hideModal"],
  components: {
    Multiselect,
  },

  computed: {
    ...mapState({
      availableVehiclesList: (state) =>
        state.superAdminStore.reImport.reImportVehicles.data,
      isLoading: (state) =>
        state.superAdminStore.reImport.reImportVehicles.status === "fetching",
    }),

    isFormValid() {
      return this.selectedVehicles.length > 0;
    },
  },

  data() {
    return {
      form: {
        id: null,
        description: "",
      },
      selectedVehicle: null,
      selectedVehicles: [],
      availableVehicles: [],
      filteredVehicles: [],
      vehicleFields: [
        { key: "vinNumber", label: "VIN" },
        { key: "make", label: "Make" },
        { key: "model", label: "Model" },
        { key: "year", label: "Year" },
        { key: "actions", label: "Actions", class: "text-center" },
      ],
    };
  },

  created() {
    this.getAvailableVehicles();
  },

  mounted() {
    if (this.reImportItem && this.reImportItem.id) {
      this.formatForm();
    } else {
      this.resetForm();
    }
  },

  methods: {
    getAvailableVehicles(searchQuery = "") {
      this.$store
        .dispatch(GET_RE_IMPORT_VEHICLES, { search: searchQuery })
        .then(() => {
          this.availableVehicles = this.availableVehiclesList;
        })
        .catch(() => {
          this.makeToast(
            "Warning",
            "Could not load available vehicles",
            "warning"
          );
        });
    },

    searchVehicles(query) {
      if (!query || query.trim() === "") {
        this.filteredVehicles = [];
        this.getAvailableVehicles(); // Reset to all vehicles
        return;
      }

      // Si la consulta tiene al menos 5 caracteres, buscar en el servidor
      if (query.trim().length >= 5) {
        this.getAvailableVehicles(query);
        return;
      }

      // Para consultas más cortas, filtrar localmente
      this.filteredVehicles = this.availableVehicles.filter((vehicle) => {
        const vinMatch =
          vehicle.vinNumber &&
          vehicle.vinNumber.toLowerCase().includes(query.toLowerCase());

        const makeMatch =
          vehicle.make &&
          vehicle.make.toLowerCase().includes(query.toLowerCase());

        const modelMatch =
          vehicle.model &&
          vehicle.model.toLowerCase().includes(query.toLowerCase());

        return vinMatch || makeMatch || modelMatch;
      });
    },

    addVehicleToSelected(vehicle) {
      if (!vehicle) return;

      const exists = this.selectedVehicles.some((v) => v.id === vehicle.id);

      if (!exists) {
        this.selectedVehicles.push(vehicle);
      }

      this.$nextTick(() => {
        this.selectedVehicle = null;
      });
    },

    removeVehicle(index) {
      this.selectedVehicles.splice(index, 1);
    },

    formatForm() {
      if (this.reImportItem.id) {
        this.form.id = this.reImportItem.id;
        this.form.description = this.reImportItem.description || "";

        if (
          this.reImportItem.vehicles &&
          this.reImportItem.vehicles.length > 0
        ) {
          this.selectedVehicles = [...this.reImportItem.vehicles];
        }
      }
    },

    resetForm() {
      this.form = {
        id: null,
        description: "",
      };
      this.selectedVehicle = null;
      this.selectedVehicles = [];
    },

    save() {
      if (!this.isFormValid) return;

      const payload = {
        id: this.reImportItem?.id || null,
        description: this.form.description,
        vehicles: this.selectedVehicles,
      };

      this.$store
        .dispatch(POST_REIMPORT_DATA, payload)
        .then(() => {
          this.makeToast(
            "Success",
            "Re-import created successfully",
            "success"
          );
          this.resetForm();
          this.refreshData();
          this.hideModal();
        })
        .catch(() => {
          this.$root.$bvToast.toast("Internal server error, try again later", {
            title: "Warning",
            variant: "warning",
          });
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.append-input {
  max-width: 40%;
}
</style>
